import { ChangeDetectorRef, Component, Inject, Input, OnInit, PLATFORM_ID } from '@angular/core';
import { CmsPartialComponentClass } from 'lib/components/cms/cms-partial-component.class';
import { CategoryService } from 'lib/services/category/category.service';
import { RouterService } from 'lib/services/router.service';
import { WebpService } from 'lib/services/webp.service';
import { CommonModule, isPlatformBrowser, Location } from '@angular/common';
import { RouterModule } from '@angular/router';
import { ExxComError } from 'lib/classes/exxcom-error.class';
import { BootstrapMarketoService } from 'lib/services/bootstrap/bootstrap-marketo.service';
import { ConfiguratorService } from 'lib/services/configuration/configurator.service';
import { isConfigurationLive } from 'lib/services/configuration/configuration-status.util';
import { getMpnLookupCandidates } from 'lib/services/webstore-product/product-url.util';
import { SpecialModule } from 'lib/modules/special.module';
import { ButtonComponent } from '../../../../../components/button/button.component';
import { ALLOWED_ICON_ORIGINS } from '../../../../../app-icon-origins';
import { FeaturedSolutionTagsComponent } from '../../../../../components/featured-solution-tags/featured-solution-tags.component';
import { ActiveTag, SOLUTION_TAGS_FIELD, getActiveTags } from '../../../../../components/featured-solution-tags/featured-solution-tags.util';
import get from 'lodash-es/get';
import { getFeatureSolutionCards } from './feature-solutions-row.util';

const scriptName = 'cms-partial-category-top-feature-solutions.component';

@Component({
    selector: 'cms-partial-category-top-feature-solutions',
    templateUrl: './feature-solutions.component.html',
    styleUrls: ['./feature-solutions.component.scss'],
    standalone: true,
    imports: [CommonModule, RouterModule, SpecialModule, ButtonComponent, FeaturedSolutionTagsComponent],
})
export class FeatureSolutionsComponent extends CmsPartialComponentClass implements OnInit {
    @Input() featureSolutionsRow: any;
    @Input() dataUrl: string;
    @Input() dataTitle: string;
    @Input() solutionID: string;
    @Input() grayBackgroundRows: any = [];
    getWebpImg: (src: string) => string;
    configuratorService: ConfiguratorService;
    /** The cards this row renders: its v2 instances when it has any, otherwise its v1 cards. */
    solutions: any[] = [];
    /** Live tags per card, same index as the card loop. Expired and unknown tags are already gone. */
    activeTags: ActiveTag[][] = [];
    /** Any card in the row has a live tag, so every card reserves the tag row to keep titles aligned. */
    rowHasTags = false;
    private iconDataUris = new Map<string, string>();

    constructor(
        @Inject('environment') environment: any,
        @Inject(PLATFORM_ID) private platformId: string,
        public bootstrapMarketoService: BootstrapMarketoService,
        routerService: RouterService,
        webpService: WebpService,
        location: Location,
        categoryService: CategoryService,
        configuratorService: ConfiguratorService,
        private cdr: ChangeDetectorRef
    ) {
        super({
            dependencies: {
                environment,
                bootstrapMarketoService,
                routerService,
                location,
                categoryService,
                webpService,
            },
        });
        this.getWebpImg = (src: string) => webpService.getWebpImg(src);
        this.configuratorService = configuratorService;
    }

    async ngOnInit() {
        // Runs on the server and again in the browser, each with its own clock. The tag
        // list itself lives in a component that skips hydration, so the two renders
        // disagreeing (a tag expiring while the SSR page sat in cache) is harmless.
        this.solutions = getFeatureSolutionCards(this.featureSolutionsRow);
        this.resolveTags();

        if (!isPlatformBrowser(this.platformId)) {
            // The configurator lookups mutate the solutions and can finish after SSR serializes
            // the page; keep them in the browser so server and client start from the same state
            return;
        }
        this.fetchIcons();

        if (this.solutions.length) {
            await Promise.all(
                this.solutions.map(async (solution: any) => {
                    // Resolved by the parent partial for every row, synchronously in its
                    // ngOnChanges, so it is set by the time @defer creates this component no
                    // matter when that happens (ST-3811). Comes from the solution page URL only,
                    // so it is the MPN the Configure button uses.
                    const fullMpn = solution.configuratorMpn;

                    // null: the URL is not a product page, so there is no configurator. Skip the
                    // lookup, which would otherwise fetch every exx config with no mpn filter, and
                    // leave isActive unset so the Configure button does not render.
                    if (!fullMpn) return;

                    // Comparisons are case-insensitive, matching the product page — a casing
                    // difference used to find no config and silently hide the Configure button.
                    for (const candidate of getMpnLookupCandidates(fullMpn)) {
                        // The v3-cutover lookup is slow, and a transient null (an aborted
                        // or failed request) would silently cost the card its Configure
                        // button and price, so retry once.
                        let configs = await this.configuratorService.getConfigurationByUrlComponent('exx', candidate, null, null, true, true);
                        if (!configs) {
                            configs = await this.configuratorService.getConfigurationByUrlComponent('exx', candidate, null, null, true, true);
                        }
                        const matchingConfig = configs?.find(
                            (config: any) => config?.mpn && fullMpn.toLowerCase().startsWith(config.mpn.toLowerCase()) && isConfigurationLive(config)
                        );
                        if (!matchingConfig) continue;

                        solution.isActive = true;
                        // The matched doc already carries the good-tier price snapshot;
                        // the old follow-up fetch re-requested the same doc and doubled
                        // the abort window.
                        if (solution.product) solution.product.price = matchingConfig.optimizedConfigurations?.good?.priceTotal;
                        break;
                    }
                })
            );
            this.cdr.markForCheck();
        } else {
            console.log('No feature solutions to process.');
        }
    }

    getBackgroundImage(index: number) {
        try {
            const rates = ['good', 'better', 'best'];
            return `https://assets.exxactcorp.com/img/exx/cms/system-images/${rates[index]}.png`;
        } catch (err) {
            console.error(...new ExxComError(213321, scriptName, err).stamp());
        }
    }

    splitWord(title: string): { firstPart: string; lastPart: string } {
        const words = title.trim().split(' ');
        const lastWord = words.pop() || '';
        const firstPart = words.join(' ');
        return { firstPart, lastPart: lastWord };
    }

    onButtonClick(event: Event) {
        event.stopPropagation();
        // event.preventDefault();
        // Add any additional logic you need here
    }

    encodeConfiguratorUrl(baseUrl: string, mpn: string): string {
        return baseUrl + encodeURIComponent(mpn);
    }

    /**
     * One clock for the whole row so the chips and the "row has tags" flag always agree:
     * the flag comes from the filtered lists, never the raw data, so a row whose tags have
     * all expired stops reserving tag space.
     */
    private resolveTags() {
        const now = Date.now();
        this.activeTags = this.solutions.map((solution: any) => getActiveTags(get(solution, SOLUTION_TAGS_FIELD), now));
        this.rowHasTags = this.activeTags.some((tags) => tags.length > 0);
    }

    getIconUrl(href: string): string {
        return this.iconDataUris.get(href) || '';
    }

    private fetchIcons() {
        for (const solution of this.solutions) {
            for (const props of get(solution, 'system_value_props', [])) {
                this.fetchIcon(get(props, 'icon.href', ''));
            }
        }
    }

    private fetchIcon(href: string) {
        if (!href || this.iconDataUris.has(href)) return;
        try {
            if (!ALLOWED_ICON_ORIGINS.has(new URL(href).origin)) return;
        } catch {
            return;
        }
        this.iconDataUris.set(href, '');
        fetch(href)
            .then((r) => r.text())
            .then((svg) => {
                this.iconDataUris.set(href, `url('data:image/svg+xml;base64,${btoa(svg)}')`);
                this.cdr.markForCheck();
            })
            .catch(() => {});
    }
}
